import { Cell, Row, Tag, Typography } from "@hellocarbo/lasagne";
import { cn } from "@hellocarbo/lasagne/utils";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { CategoryScore, TCategory } from "@/types";
import { comparisonCarbonRatio, formatCarbon } from "@/utils/carbon";

interface ComparisonRowProps {
  isSubHeader?: boolean;
  categoryCurrent: CategoryScore;
  categoryCompared: CategoryScore | undefined;
}

function ComparisonRow({
  isSubHeader,
  categoryCurrent,
  categoryCompared,
}: ComparisonRowProps) {
  const { t } = useTranslation(["categories", "common"]);

  const currentCarbon = categoryCurrent ? Number(categoryCurrent.carbon) : 0;
  const comparedCarbon = categoryCompared
    ? Number(categoryCompared?.carbon)
    : 0;

  const categoryCurrentNullish =
    !categoryCurrent || !categoryCurrent.carbon || currentCarbon === 0;
  const categoryComparedNullish =
    !categoryCompared || !categoryCompared.carbon || comparedCarbon === 0;

  const bothCategoriesNullish =
    categoryCurrentNullish && categoryComparedNullish;

  const oneCategoryNullish = categoryCurrentNullish || categoryComparedNullish;

  if (bothCategoriesNullish) {
    return null;
  }

  const carbonDifference = comparedCarbon - currentCarbon;
  const comparedCarbonRatio =
    categoryCompared &&
    comparisonCarbonRatio({
      x: comparedCarbon,
      y: currentCarbon,
    });
  const { formattedCarbon: formattedCarbonCurrent, unit: unitCurrent } =
    formatCarbon(t, currentCarbon);

  const { formattedCarbon: formattedCarbonCompared, unit: unitCompared } =
    formatCarbon(t, comparedCarbon);

  const { formattedCarbon: formattedCarbonDifference, unit: unitDifference } =
    formatCarbon(t, Math.abs(carbonDifference));

  return (
    <Row
      key={categoryCurrent.category.key}
      className={cn(isSubHeader && "bg-grayscale-xx-light")}
    >
      <Cell className="pl-xs">
        <Typography step="-1" weight={isSubHeader ? "semibold" : "normal"}>
          {t(`categories:${categoryCurrent.category.key}`)}
        </Typography>
      </Cell>
      <Cell>
        {categoryCurrentNullish ? (
          <Typography
            step="-1"
            weight="semibold"
            className="text-grayscale-light"
          >
            -
          </Typography>
        ) : (
          <Typography step="-1" weight={isSubHeader ? "semibold" : "normal"}>
            {formattedCarbonCurrent} &nbsp;
            {t(`common:unit.${unitCurrent}`)}
          </Typography>
        )}
      </Cell>
      <Cell>
        {categoryComparedNullish ? (
          <Typography
            step="-1"
            weight="semibold"
            className="text-grayscale-light"
          >
            -
          </Typography>
        ) : (
          <Typography step="-1" weight={isSubHeader ? "semibold" : "normal"}>
            {formattedCarbonCompared} &nbsp;
            {t(`common:unit.${unitCompared}`)}
          </Typography>
        )}
      </Cell>
      <Cell /* We display the comparison cell only when we have 2 categories defined and with different values */
      >
        {oneCategoryNullish || !carbonDifference || !comparedCarbonRatio ? (
          <Typography
            step="-1"
            weight="semibold"
            className="text-grayscale-light"
          >
            -
          </Typography>
        ) : (
          <div className="space-x-xs">
            <Typography step="-1" weight={isSubHeader ? "semibold" : "normal"}>
              {carbonDifference >= 0 ? "+" : "-"}
              {formattedCarbonDifference}
              &nbsp;
              {t(`common:unit.${unitDifference}`)}
              &nbsp;
            </Typography>
            <Tag
              text={t("formatter:percentage", {
                value: comparedCarbonRatio,
              })}
              intent={comparedCarbonRatio < 0 ? "positive" : "negative"}
              PrefixIcon={
                comparedCarbonRatio < 0 ? ArrowDownRight : ArrowUpRight
              }
            />
          </div>
        )}
      </Cell>
    </Row>
  );
}

export function InternalComparison({
  currentCategoryScores,
  comparedCategoryScores,
  allCategoriesHierarchized,
}: {
  allCategoriesHierarchized: Record<number, TCategory>;
  currentCategoryScores: CategoryScore[];
  comparedCategoryScores: CategoryScore[];
}) {
  const indexedCategoriesCurrent = currentCategoryScores.reduce(
    (acc: Record<string, CategoryScore>, categoryScore) => {
      acc[categoryScore.category.key] = categoryScore;
      return acc;
    },
    {}
  );

  const indexedCategoriesCompared = comparedCategoryScores.reduce(
    (acc: Record<string, CategoryScore>, categoryScore) => {
      acc[categoryScore.category.key] = categoryScore;
      return acc;
    },
    {}
  );

  const allTopCategories = Object.values(allCategoriesHierarchized);
  return (
    <>
      {allTopCategories.map((topCategory) => {
        return (
          <Fragment key={topCategory.key}>
            <ComparisonRow
              categoryCurrent={indexedCategoriesCurrent[topCategory.key]}
              categoryCompared={indexedCategoriesCompared[topCategory.key]}
              isSubHeader={true}
            />

            {topCategory.children?.map((subCategory) => (
              <ComparisonRow
                categoryCurrent={indexedCategoriesCurrent[subCategory.key]}
                categoryCompared={indexedCategoriesCompared[subCategory.key]}
                isSubHeader={false}
              />
            ))}
          </Fragment>
        );
      })}
    </>
  );
}
